<template>
  <div class="admin-page">
    <div class="admin-page-inner">
      <div class="admin-page-header">
        <div>
          <span class="admin-kicker">Datenverwaltung</span>
          <h1 class="admin-page-title">Verträge</h1>
        </div>
      </div>

      <div>
        <div v-if="loading" class="flex items-center justify-center py-20">
          <div class="text-center">
            <div class="inline-block animate-spin rounded-full h-12 w-12 border-4 border-gray-300 border-t-caspyellow mb-4"></div>
            <p class="text-gray-600 font-medium">Lade Verträge...</p>
          </div>
        </div>
        
        <div v-else class="space-y-6">
          <div class="bg-white rounded-2xl shadow-lg hover:shadow-xl transition-shadow duration-300 overflow-hidden border border-gray-200">
            <div class="px-6 py-4" style="background-color: #5f656e;">
              <h3 class="text-2xl font-bold text-white">Neuer Vertrag</h3>
            </div>
            <div class="p-6 space-y-6">
              <div class="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <div class="space-y-2">
                  <label class="block text-sm font-semibold text-gray-700">Vertragsbeschreibung</label>
                  <input
                    v-model="newVertrag.vertragsbeschreibung"
                    class="w-full border-2 border-gray-300 rounded-xl px-4 py-3 focus:border-caspyellow focus:ring-2 focus:ring-caspyellow focus:ring-opacity-20 focus:outline-none transition-all duration-200"
                    placeholder="Vertragsbeschreibung (eindeutig)"
                  />
                </div>
                <div class="space-y-2">
                  <label class="block text-sm font-semibold text-gray-700">Vertrag aktiv</label>
                  <label class="relative inline-flex items-center cursor-pointer mt-2">
                    <input type="checkbox" v-model="newVertrag.active" class="sr-only peer">
                    <div class="w-14 h-7 bg-gray-300 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-green-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-0.5 after:left-[4px] after:bg-white after:rounded-full after:h-6 after:w-6 after:transition-all after:shadow-md peer-checked:bg-green-600"></div>
                    <span class="ml-3 text-sm font-medium text-gray-700">{{ newVertrag.active ? 'Aktiv' : 'Inaktiv' }}</span>
                  </label>
                </div>
              </div>
              
              <div class="space-y-2">
                <label class="block text-sm font-semibold text-gray-700">Zugeordnete Tarife</label>
                <div class="border-2 border-gray-300 rounded-xl p-4 bg-gray-50 max-h-56 overflow-y-auto">
                  <div class="flex flex-wrap gap-2">
                    <button
                      v-for="tarif in verrechnungstarife"
                      :key="tarif.id"
                      type="button"
                      class="admin-tab"
                      :class="{ 'admin-tab--active': newVertrag.tarifIds.includes(tarif.id) }"
                      @click="toggleTarif(newVertrag.tarifIds, tarif.id)"
                    >
                      {{ tarif.name }}
                    </button>
                  </div>
                  <div v-if="verrechnungstarife.length === 0" class="text-gray-400 text-center py-4">
                    Keine Tarife verfügbar
                  </div>
                </div>
              </div>
              
              <div class="flex justify-end pt-2">
                <button
                  type="button"
                  @click="createVertrag"
                  :disabled="!newVertrag.vertragsbeschreibung.trim()"
                  class="admin-button--primary"
                >
                  Vertrag erstellen
                </button>
              </div>
            </div>
          </div>

          <div class="bg-white rounded-2xl shadow-lg border border-gray-200 p-4">
            <div class="flex flex-col sm:flex-row sm:items-center gap-3">
              <label for="contract-name-filter" class="text-sm font-semibold text-gray-700 shrink-0">
                Verträge filtern
              </label>
              <div class="relative flex-1">
                <input
                  id="contract-name-filter"
                  v-model="contractNameFilter"
                  type="search"
                  class="w-full border-2 border-gray-300 rounded-xl px-4 py-2.5 pr-10 focus:border-caspyellow focus:ring-2 focus:ring-caspyellow focus:ring-opacity-20 focus:outline-none transition-all duration-200"
                  placeholder="Nach Vertragsbeschreibung suchen"
                />
                <button
                  v-if="contractNameFilter"
                  type="button"
                  class="absolute right-2 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-800 px-2"
                  aria-label="Vertragsfilter löschen"
                  @click="contractNameFilter = ''"
                >
                  <i class="fa fa-xmark"></i>
                </button>
              </div>
              <span class="text-sm text-gray-500 shrink-0">
                {{ filteredVertraege.length }} von {{ vertraege.length }}
              </span>
            </div>
          </div>

          <div v-for="vertrag in filteredVertraege" :key="vertrag.id" class="bg-white rounded-2xl shadow-lg hover:shadow-xl transition-all duration-300 overflow-hidden border border-gray-200">
            <div v-if="editingVertrag === vertrag.id" class="p-6 space-y-6">
              <div class="grid grid-cols-1 lg:grid-cols-2 gap-6">
                <div class="space-y-2">
                  <label class="block text-sm font-semibold text-gray-700">Vertragsbeschreibung</label>
                  <input
                    v-model="editVertragData.vertragsbeschreibung"
                    class="w-full border-2 border-gray-300 rounded-xl px-4 py-3 focus:border-caspyellow focus:ring-2 focus:ring-caspyellow focus:ring-opacity-20 focus:outline-none transition-all duration-200"
                  />
                </div>
                <div class="space-y-2">
                  <label class="block text-sm font-semibold text-gray-700">Vertrag aktiv</label>
                  <label class="relative inline-flex items-center cursor-pointer mt-2">
                    <input type="checkbox" v-model="editVertragData.active" class="sr-only peer">
                    <div class="w-14 h-7 bg-gray-300 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-green-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-0.5 after:left-[4px] after:bg-white after:rounded-full after:h-6 after:w-6 after:transition-all after:shadow-md peer-checked:bg-green-600"></div>
                    <span class="ml-3 text-sm font-medium text-gray-700">{{ editVertragData.active ? 'Aktiv' : 'Inaktiv' }}</span>
                  </label>
                </div>
              </div>
              
              <div class="space-y-2">
                <label class="block text-sm font-semibold text-gray-700">Zugeordnete Tarife</label>
                <div class="border-2 border-gray-300 rounded-xl p-4 bg-gray-50 max-h-56 overflow-y-auto">
                  <div class="flex flex-wrap gap-2">
                    <button
                      v-for="tarif in verrechnungstarife"
                      :key="tarif.id"
                      type="button"
                      class="admin-tab"
                      :class="{ 'admin-tab--active': editVertragData.tarifIds.includes(tarif.id) }"
                      @click="toggleTarif(editVertragData.tarifIds, tarif.id)"
                    >
                      {{ tarif.name }}
                    </button>
                  </div>
                </div>
              </div>
              
              <div class="flex gap-3 justify-end pt-2">
                <button
                  type="button"
                  @click="cancelEditVertrag"
                  class="admin-button--secondary"
                >
                  Abbrechen
                </button>
                <button
                  type="button"
                  @click="saveVertrag(vertrag.id)"
                  class="admin-button--primary"
                >
                  Speichern
                </button>
              </div>
            </div>
            
            <div v-else class="p-6">
              <div class="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-4">
                <div class="flex-1 space-y-3">
                  <h4 class="text-2xl font-bold text-gray-800">{{ vertrag.vertragsbeschreibung }}</h4>
                  <p class="text-base font-semibold text-purple-600 font-mono">APP-ID: APV-{{ vertrag.id }}</p>
                  <div class="flex items-center gap-2">
                    <span class="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium"
                      :class="vertrag.active !== false ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'">
                      Vertrag: {{ vertrag.active !== false ? 'Aktiv' : 'Inaktiv' }}
                    </span>
                  </div>
                  <div v-if="vertrag.tarife && vertrag.tarife.length > 0" class="space-y-2">
                    <span class="text-sm font-semibold text-gray-700">Zugeordnete Tarife:</span>
                    <div class="flex flex-wrap gap-2">
                      <span v-for="tarif in vertrag.tarife" :key="tarif.id" 
                            class="inline-flex items-center px-3 py-1 rounded-lg text-sm font-medium bg-green-100 text-green-800">
                        {{ tarif.name }}
                      </span>
                    </div>
                  </div>
                  <div v-if="vertrag.einsatzorte && vertrag.einsatzorte.length > 0" class="space-y-2 mt-4">
                    <span class="text-sm font-semibold text-gray-700">Einsatzorte & Einsatzstellen:</span>
                    <div class="border-2 border-gray-200 rounded-lg p-3 bg-gray-50">
                      <div v-for="einsatzort in vertrag.einsatzorte" :key="einsatzort.id" class="mb-3 last:mb-0">
                        <div class="flex items-start gap-2">
                          <svg class="w-4 h-4 text-blue-600 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z"/>
                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 11a3 3 0 11-6 0 3 3 0 016 0z"/>
                          </svg>
                          <div class="flex-1">
                            <div class="font-medium text-gray-800">{{ einsatzort.einsatzort_beschreibung }}</div>
                            <div class="text-sm font-semibold text-blue-600 font-mono">APP-ID: APO-{{ einsatzort.id }}</div>
                            <div class="text-xs text-gray-500">Alter Code: {{ einsatzort.einsatzort_code }}</div>
                            <div v-if="einsatzort.einsatzstellen && einsatzort.einsatzstellen.length > 0" class="ml-4 mt-2 space-y-1">
                              <div v-for="stelle in einsatzort.einsatzstellen" :key="stelle.id" class="flex items-start gap-2 text-sm">
                                <svg class="w-3 h-3 text-green-600 mt-0.5 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2"/>
                                </svg>
                                <div>
                                  <div class="text-gray-700">{{ stelle.einsatzstelle_beschreibung }}</div>
                                  <div class="text-xs font-semibold text-green-600 font-mono">APP-ID: APS-{{ stelle.id }}</div>
                                  <div class="text-xs text-gray-500">Alter Code: {{ stelle.einsatzstelle_code }}</div>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                
                <div class="flex gap-3 lg:flex-col">
                  <button
                    type="button"
                    @click="startEditVertrag(vertrag)"
                    class="flex-1 lg:flex-none admin-button--secondary"
                  >
                    Bearbeiten
                  </button>
                  <button
                    type="button"
                    @click="viewDetails(vertrag.id)"
                    class="flex-1 lg:flex-none admin-button--secondary"
                  >
                    Details
                  </button>

                  <button
                    type="button"
                    @click="deleteVertrag(vertrag.id)"
                    class="flex-1 lg:flex-none admin-button--danger"
                  >
                    Löschen
                  </button>
                </div>
              </div>
            </div>
          </div>

          <div v-if="filteredVertraege.length === 0" class="text-center py-16">
            <div class="inline-flex items-center justify-center w-16 h-16 rounded-full bg-gray-200 mb-4">
              <svg class="w-8 h-8 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"/>
              </svg>
            </div>
            <p class="text-gray-500 text-lg font-medium">
              {{ contractNameFilter ? 'Keine passenden Verträge gefunden' : 'Keine Verträge vorhanden' }}
            </p>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import CASP from '../../CASP';

export default {
  data() {
    return {
      loading: true,
      vertraege: [],
      contractNameFilter: '',
      verrechnungstarife: [],
      newVertrag: {
        vertragsbeschreibung: '',
        navisionsync: false,
        active: true,
        tarifIds: []
      },
      editingVertrag: null,
      editVertragData: {
        vertragsbeschreibung: '',
        navisionsync: false,
        active: true,
        tarifIds: []
      }
    };
  },
  computed: {
    filteredVertraege() {
      const query = this.contractNameFilter.trim().toLocaleLowerCase();
      if (!query) return this.vertraege;
      return this.vertraege.filter(vertrag =>
        String(vertrag.vertragsbeschreibung || '').toLocaleLowerCase().includes(query)
      );
    }
  },
  methods: {
    toggleTarif(tarifIds, tarifId) {
      const index = tarifIds.indexOf(tarifId);
      if (index > -1) {
        tarifIds.splice(index, 1);
      } else {
        tarifIds.push(tarifId);
      }
    },
    loadData() {
      this.loading = true;
      let vertraegeLoaded = false;
      let tarifeLoaded = false;
      
      CASP.getVertraege((status, response) => {
        if (status === 200) {
          this.vertraege = response;
          vertraegeLoaded = true;
          if (tarifeLoaded) this.loading = false;
        } else {
          console.error('Fehler beim Laden der Verträge:', response);
          alert('Fehler beim Laden der Verträge: ' + (response?.error || response));
          this.loading = false;
        }
      });
      
      CASP.getTarife((status, response) => {
        if (status === 200) {
          this.verrechnungstarife = response.verrechnungstarife;
          tarifeLoaded = true;
          if (vertraegeLoaded) this.loading = false;
        } else {
          console.error('Fehler beim Laden der Tarife:', response);
          this.loading = false;
        }
      });
    },
    createVertrag() {
      if (!this.newVertrag.vertragsbeschreibung.trim()) return;
      CASP.createVertrag(this.newVertrag, (status, response) => {
        if (status === 200) {
          this.newVertrag = {
            vertragsbeschreibung: '',
            navisionsync: false,
            active: true,
            tarifIds: []
          };
          this.loadData();
        } else {
          console.error('Fehler beim Erstellen des Vertrags:', response);
          alert('Fehler beim Erstellen des Vertrags: ' + (response?.error || response));
        }
      });
    },

    saveVertrag(id) {
      CASP.updateVertrag(id, this.editVertragData, (status, response) => {
        if (status === 200) {
          this.editingVertrag = null;
          this.loadData();
        } else {
          console.error('Fehler beim Speichern des Vertrags:', response);
          alert('Fehler beim Speichern des Vertrags: ' + (response?.error || response));
        }
      });
    },
    startEditVertrag(vertrag) {
      this.editingVertrag = vertrag.id;
      this.editVertragData = {
        vertragsbeschreibung: vertrag.vertragsbeschreibung,
        navisionsync: vertrag.navisionsync || false,
        active: vertrag.active !== false,
        tarifIds: vertrag.tarife ? vertrag.tarife.map(tarif => tarif.id) : [],
        debitor_id: vertrag.debitor_id || null
      };
    },
    cancelEditVertrag() {
      this.editingVertrag = null;
      this.editVertragData = {
        vertragsbeschreibung: '',
        navisionsync: false,
        active: true,
        tarifIds: []
      };
    },
    deleteVertrag(id) {
      if (!confirm('Möchten Sie diesen Vertrag wirklich löschen?')) return;
      CASP.deleteVertrag(id, (status, response) => {
        if (status === 200) {
          this.loadData();
        } else {
          console.error('Fehler beim Löschen des Vertrags:', response);
          alert('Fehler beim Löschen des Vertrags: ' + (response?.error || response));
        }
      });
    },
    viewDetails(id) {
      this.$router.push({ name: 'AdminDataContractDetails', params: { id } });
    }
  },
  created() {
    document.getElementById("mainspinner").style.display = "none";
    if (localStorage) localStorage.setItem("last", "/admin/data/vertraege");
    this.loadData();
  }
};
</script>

<style scoped>
.hyphens-auto {
  hyphens: auto;
}
</style>
